import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { DragPreviewImage } from 'react-dnd';
import { MediaDimensions } from '../../stores/types/types';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import { StylePackContentType, mediaTemplates, templateLabels } from '../style-packs/style-packs-tab';

interface ContentCardProps {
	url: string;
	thumbnail?: string;
	altText: string;
	slug?: string;
	provider?: string;
	customSize?: Required<MediaDimensions>;
	contentType?: string;
	canDrag?: boolean;
}

export default function ContentCard({
	url,
	thumbnail,
	altText,
	customSize,
	slug,
	provider,
	contentType = '',
	canDrag = true,
}: ContentCardProps) {
	const [mouseTouched, setMouseTouched] = useState<boolean>(false);
	const pauseTimeout = useRef<NodeJS.Timeout>();

	const contentTypeText = useMemo(() => {
		if (contentType === StylePackContentType.TITLE) {
			return 'Lower';
		}
		return templateLabels[contentType as StylePackContentType];
	}, [contentType, templateLabels]);

	const dndProps = {
		id: url,
		type: mediaTemplates.includes(contentType as StylePackContentType)
			? DRAG_TYPE.FEED_MEDIA
			: DRAG_TYPE.TITLE,
		name: `${altText} - ${contentTypeText}`,
		template: slug,
		provider,
	};

	const videoRef = useRef<HTMLVideoElement>(null);
	const [thumbnailString, setThumbnailString] = useState<string>(thumbnail ?? '');

	const { dragRef, dragPreview } = useDragItem(dndProps, canDrag);

	const createImageObject = (imageUrl: string): Promise<HTMLImageElement> => new Promise(
		(resolve, reject) => {
			const image = new Image();
			image.crossOrigin = 'anonymous';
			image.src = imageUrl;
			image.onload = () => resolve(image);
			image.onerror = reject;
		},
	);

	const createImageThumbnail = async (imageUrl: string) => {
		const image = await createImageObject(imageUrl);
		if (image) {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			canvas.height = slug === 'brandlive-selfridges'
				? image.height * 0.75
				: image.height * 0.5;
			canvas.width = slug === 'brandlive-selfridges'
				? image.width * 0.75
				: image.width * 0.5;
			if (!ctx) {
				return thumbnail;
			}

			ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
			return canvas.toDataURL();
		}
		return undefined;
	};

	useEffect(() => {
		if (!thumbnail) return;

		createImageThumbnail(thumbnail)
			.then((base64) => {
				if (base64) {
					setThumbnailString(base64);
				} else {
					setThumbnailString(thumbnail);
				}
			})
			.catch(() => {
				setThumbnailString(thumbnail);
			});
	}, [thumbnailString]);

	return (
		<>
			<div
				className="content-card"
				style={{ cursor: canDrag ? 'grab' : 'default' }}
				data-testid="content-card"
				ref={dragRef}
				onMouseEnter={() => {
					clearTimeout(pauseTimeout.current);
					setMouseTouched(true);
				}}
				onMouseLeave={() => {
					pauseTimeout.current = setTimeout(() => {
						setMouseTouched(false);
					}, 1000);
				}}
			>
				<div className="image-container">
					<video
						className="no-select"
						style={customSize}
						src={mouseTouched ? url : ''}
						ref={videoRef}
						data-testid="content-card-video"
						autoPlay={true}
						poster={thumbnail}
					/>
				</div>
			</div>
			{thumbnail && (
				<DragPreviewImage
					connect={dragPreview}
					src={thumbnailString}
				/>
			)}
		</>
	);
}
